import React from 'react';
import { connect } from 'react-redux';

import { AppState } from 'src/config/appState';
import { StepButtons, StepButtonsProps } from 'src/modules/programs/components/StepButtons';
import { ProgramEnum } from 'src/modules/programs/models/programs';
import * as S from 'src/modules/programs/stepComponents/styles';
import {
    GenericSurveyAnswer,
    SurveyDummyQuestion,
} from 'src/modules/programs/surveys/models/surveys';

import { StepModal } from '../../components/StepModal';
import { submitProgramSurvey } from '../actions/surveysActions';
import { ProgramSurvey } from '../components/ProgramSurvey';
import { GenericProgramSurvey } from '../models/surveysGeneric';
import { getProgramSurveyAnswers, isProgramSurveyAnswered } from '../selectors/surveysSelectors';

interface State {
    isModalOpen: boolean;
}

interface StateProps {
    answers: GenericSurveyAnswer[] | undefined;
    isSurveyAnswered: boolean;
}

interface ActionsProps {
    submitProgramSurvey: typeof submitProgramSurvey;
}

interface Props extends StepButtonsProps, StateProps, ActionsProps {
    programId: ProgramEnum;
    surveyId: GenericProgramSurvey;
    heading?: React.ReactNode;
    postSurvey?: React.ReactNode;
    modalContent?: React.ReactNode;
    showQuestionsIndexes?: boolean;
    wrapQuestions?: boolean;
    dummyQuestion?: SurveyDummyQuestion;
}

class SimpleSurveyStepContainer extends React.Component<Props, State> {
    public state: State = {
        isModalOpen: false,
    };

    public handleToggleModal = () => {
        this.setState(state => ({ isModalOpen: !state.isModalOpen }));
    };

    private handleSubmit = () => {
        const { programId, surveyId, answers, goToNextStep } = this.props;

        if (!answers) {
            return;
        }

        this.props.submitProgramSurvey({
            programId,
            surveyId,
            answers,
        });

        goToNextStep();
    };

    public render(): JSX.Element {
        const {
            programId,
            surveyId,
            isSurveyAnswered,
            heading,
            postSurvey,
            goToPreviousStep,
            modalContent,
            showQuestionsIndexes,
            wrapQuestions,
            dummyQuestion,
        } = this.props;

        return (
            <>
                <S.ContentRow style={{ flex: 1 }}>
                    <S.ContentColumn>
                        {heading}
                        <ProgramSurvey
                            programId={programId}
                            surveyId={surveyId}
                            postSurvey={postSurvey}
                            showQuestionsIndexes={showQuestionsIndexes}
                            wrapQuestions={wrapQuestions}
                            dummyQuestion={dummyQuestion}
                        />
                    </S.ContentColumn>
                </S.ContentRow>
                {modalContent && (
                    <StepModal
                        centeredText
                        isVisible={this.state.isModalOpen}
                        onCancel={this.handleToggleModal}
                        nextStep={this.handleSubmit}
                    >
                        {modalContent}
                    </StepModal>
                )}
                <StepButtons
                    onPreviousClick={goToPreviousStep}
                    onNextClick={modalContent ? this.handleToggleModal : this.handleSubmit}
                    nextDisabled={!isSurveyAnswered}
                    hasNextStep
                />
            </>
        );
    }
}

const mapStateToProps = (state: AppState, { programId, surveyId }: Props): StateProps => ({
    answers: getProgramSurveyAnswers(state, programId, surveyId),
    isSurveyAnswered: isProgramSurveyAnswered(state, programId, surveyId),
});

export const SimpleSurveyStep = connect<StateProps, ActionsProps>(mapStateToProps, {
    submitProgramSurvey,
})(SimpleSurveyStepContainer);
